import React, { useEffect } from 'react';
import { AppBar, Toolbar, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import { useAuth } from '../AuthContext';
import { landingPathFor } from '../usePermissions';
import useCareersBrand from '../useCareersBrand';
import OrganizationLogo from './OrganizationLogo';
import { brand as productBrand } from '../brand';
import Ituka from './Ituka';

/**
 * The bar across the top of an organization's careers pages.
 *
 * It says whose page this is: their logo if they have one, their name if not. The
 * product's own name steps back to a small "Powered by RoRi" on the right, and
 * comes forward again only where the address names no organization at all.
 *
 * Always light, whatever mode the rest of the product is in. A logo is designed
 * for a particular background, almost always a white one, and a dark bar would
 * turn most of them into a smudge. The page below follows the person's mode as it
 * always did.
 *
 * Nothing is drawn on the left while the organization is still loading, rather
 * than the product's name that is then replaced a moment later.
 */
const INK = '#1a1a1a';

const CareersBrandHeader = ({ slug, jobId, context = 'Careers' }) => {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { brand, status } = useCareersBrand({ slug, jobId });

  // The tab says whose page it is, and goes back to what it said before.
  useEffect(() => {
    if (!brand?.name) return undefined;
    const before = document.title;
    document.title = `${brand.name} Careers`;
    return () => {
      document.title = before;
    };
  }, [brand?.name]);

  const home = brand?.slug ? `/careers/${brand.slug}` : '/careers';
  const fallback = status === 'none';

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#ffffff',
        color: INK,
        borderBottom: '1px solid #e5e7eb',
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 64, sm: 80 }, gap: 2 }}>
        <Box
          role="link"
          tabIndex={0}
          aria-label={brand?.name ? `${brand.name} careers home` : 'Careers home'}
          onClick={() => navigate(home)}
          onKeyDown={(event) => event.key === 'Enter' && navigate(home)}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
          data-testid="careers-brand"
        >
          {status === 'ready' && brand?.logoUrl && (
            <OrganizationLogo
              url={brand.logoUrl}
              name={brand.name}
              sx={{ maxHeight: { xs: 40, sm: 52 }, maxWidth: { xs: 160, sm: 260 } }}
            />
          )}
          {status === 'ready' && !brand?.logoUrl && (
            <Typography variant="h6" component="div" noWrap sx={{ fontWeight: 700, color: INK }}>
              {brand?.name}
            </Typography>
          )}
          {fallback && (
            <>
              <Ituka size={40} still label={productBrand.name} sx={{ height: { xs: 32, sm: 40 }, width: { xs: 32, sm: 40 } }} />
              <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, color: INK }}>
                {productBrand.name}
              </Typography>
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          <Typography
            variant="body2"
            sx={{ color: '#4b5563', display: { xs: 'none', sm: 'block' } }}
            data-testid="careers-context"
          >
            {context}
          </Typography>
          {user && (
            <Button size="small" onClick={() => navigate(landingPathFor(user))} sx={{ color: INK }}>
              My workspace
            </Button>
          )}
          {/* Quiet on purpose: this is their page, not ours. */}
          {!fallback && (
            <Box
              sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.75, color: '#6b7280' }}
              data-testid="careers-powered-by"
            >
              <Typography variant="caption">Powered by</Typography>
              <Ituka size={18} still />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>{productBrand.name}</Typography>
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default CareersBrandHeader;
